import { IconButton, ImportIcon, Pane, Strong, Tooltip } from 'evergreen-ui';
import React from 'react';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { Attachment } from './types';
import MessageContentRender from './Message.ContentRender';

type MessageAttachmentsProps = {
  messageId: number
  type: string
  useGetMessageAttachments: any
}

export default function MessageAttachments({ messageId, type, useGetMessageAttachments }: MessageAttachmentsProps) {
  const { data: attachments = [], isFetching } = useGetMessageAttachments(messageId, {
    skip: !messageId || type === 'outgoing' || type === 'channel',
  });

  if (isFetching) return <SuspenseSpinner />;
  if (attachments.length === 0) return <Strong>Нет вложений</Strong>;
  return (
    <Pane
      display="grid"
      gridTemplateColumns="repeat(2, 1fr)"
      gridGap=".5rem"
      overflow="auto"
      paddingRight=".5rem"
    >
      {React.Children.toArray(attachments.map((attachment: Attachment) => (
        <MessageAttachment messageId={messageId} attachment={attachment} type={type} />
      )))}
    </Pane>
  );
}

const noImage = 'data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9JzMwMHB4JyB3aWR0aD0nMzAwcHgnICBmaWxsPSIjMDAwMDAwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjAwIDIwMCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjAwIDIwMDsiIHhtbDpzcGFjZT0icHJlc2VydmUiPjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+Cgkuc3Qwe2ZpbGw6bm9uZTt9Cjwvc3R5bGU+PHBhdGggY2xhc3M9InN0MCIgZD0iTTExNS44LDExMC42bDMwLDMwLjNIMTU4YzIuNSwwLDQuNi0yLjEsNC42LTQuNnYtMTIuNGwtMjkuOS0zMC4yTDExNS44LDExMC42eiI+PC9wYXRoPjxnPjxnPjxwYXRoIGQ9Ik0zNi42LDEzNS4zVjEwOWwzNi41LTM2LjlsMjQuMSwyNC4zbDIuOC0yLjhMNzQuNSw2Ny44Yy0wLjgtMC44LTIuMS0wLjgtMi44LDBsLTM1LjEsMzUuNVY1OC41YzAtMi41LDIuMS00LjYsNC42LTQuNiAgICBoOTguNmw0LTRINDEuMWMtNC43LDAtOC42LDMuOS04LjYsOC42djc2LjhjMCw0LjcsMy45LDguNiw4LjYsOC42aDguNmw0LTRINDEuMUMzOC42LDEzOS45LDM2LjYsMTM3LjgsMzYuNiwxMzUuM3oiPjwvcGF0aD48cGF0aCBkPSJNMTU4LDQ5LjloLTIuOGwtNCw0aDYuOGMyLjUsMCw0LjYsMi4xLDQuNiw0LjZ2NTguN2wtMjguNS0yOC44Yy0wLjQtMC40LTAuOS0wLjYtMS40LTAuNmMwLDAsMCwwLDAsMCAgICBjLTAuNSwwLTEsMC4yLTEuNCwwLjZMMTEzLDEwNi43bC03LjMtNy40bC0yLjgsMi44bDM3LjMsMzcuN2gtNzVsLTQsNEgxNThjNC43LDAsOC42LTMuOSw4LjYtOC42VjU4LjUgICAgQzE2Ni42LDUzLjcsMTYyLjcsNDkuOSwxNTgsNDkuOXogTTE2Mi42LDEzNS4zYzAsMi41LTIuMSw0LjYtNC42LDQuNmgtMTIuMmwtMzAtMzAuM2wxNi44LTE2LjlsMjkuOSwzMC4yVjEzNS4zeiI+PC9wYXRoPjwvZz48cmVjdCB4PSIxNy4yIiB5PSI5Ny4yIiB0cmFuc2Zvcm09Im1hdHJpeCgwLjcwNzEgLTAuNzA3MSAwLjcwNzEgMC43MDcxIC00MC43NzgxIDk5Ljk4NTcpIiB3aWR0aD0iMTY2LjIiIGhlaWdodD0iNCI+PC9yZWN0PjwvZz48L3N2Zz4=';

// TODO: выпилить, когда будет нормально работать mimeType
function imagePrefixByFileName(fileName: string) {
  if (!fileName) return null;
  let type = '';
  const imageExp: {
    [key: string]: string
  } = {
    'svg+xml': 'svg',
    jpeg: '.jpg, .jpeg, .jfif, .pjpeg, .pjp',
    png: 'png',
    gif: 'gif',
  };
  const expansion = fileName.split('.').at(-1);
  if (!expansion) return null;
  Object.keys(imageExp).some((key) => {
    if (imageExp[key].includes(expansion)) {
      type = key;
      return true;
    }
    return false;
  });
  return type ? `data:image${type};base64,` : null;
}

type MessageAttachmentProps = {
  messageId: number
  attachment: Attachment
  type: string
}

function MessageAttachment({ messageId, attachment, type }: MessageAttachmentProps) {
  const {
    id,
    fileName,
    name,
    content,
  } = attachment;
  const imagePrefix = imagePrefixByFileName(fileName || name);
  const preview = imagePrefix && content ? `${imagePrefix}${content.data}` : noImage;
  const downloadAttachment = () => {
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_API_PREFIX}/messages${type === 'incoming' ? '-incoming' : ''}/${messageId}/attachments/${id}/data`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" border>
      <Pane padding={4} background="#edeff0" display="grid" gridTemplateColumns="1fr max-content" alignItems="center">
        <Strong>{name}</Strong>
        <Tooltip content="Скачать">
          <IconButton
            appearance="minimal"
            icon={ImportIcon}
            onClick={downloadAttachment}
          />
        </Tooltip>
      </Pane>
      {imagePrefix ? (
        <img
          width="100%"
          aria-hidden="true"
          src={preview}
          alt={name}
        />
      ) : (
        <MessageContentRender fileContent={content.data} />
      )}
    </Pane>
  );
}
